import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  User, LogOut, CheckCircle2, 
  Shield, Loader2, Edit2, Save, X, ExternalLink, Store 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QRCodeSVG } from "qrcode.react";
import { t } from "@/i18n";

export const ProfilePage = () => {
  const { user, logout, updateUser, loading: authLoading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ name: "", email: "" });
  const [saving, setSaving] = useState(false);
  
  const [startingVerification, setStartingVerification] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [verificationSessionId, setVerificationSessionId] = useState(null);

  useEffect(() => {
    const verification = searchParams.get("verification");
    const sessionId = searchParams.get("verificationSessionId");
    const status = searchParams.get("status");
    
    if (verification === "complete" || sessionId) {
      if (status === "Approved") {
        toast.success(t('toasts.verificationSuccess'));
        checkAuth();
      } else if (status === "Declined") {
        toast.error(t('toasts.verificationDeclined'));
      } else if (sessionId) {
        checkVerificationStatus(sessionId);
      }
      window.history.replaceState({}, "", "/profile");
    }
  }, [searchParams]);

  if (!authLoading && !user) {
    navigate("/auth", { replace: true });
    return null;
  }

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a4d2e]" />
      </div>
    );
  }

  const handleStartEdit = () => {
    setEditData({ name: user.name, email: user.email });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData({ name: "", email: "" });
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/auth/profile`,
        editData,
        { withCredentials: true }
      );
      updateUser(res.data);
      setIsEditing(false);
      toast.success(t('toasts.profileUpdated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toasts.errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const startAgeVerification = async () => {
    setStartingVerification(true);
    try {
      const res = await axios.post(
        `${API}/auth/start-age-verification`,
        {},
        { withCredentials: true }
      );
      
      setVerificationSessionId(res.data.session_id);
      window.open(res.data.verification_url, "_blank");
      toast.success(t('ageVerification.starting'));
      
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toasts.errorOccurred'));
    } finally {
      setStartingVerification(false);
    }
  };

  const checkVerificationStatus = async (sessionId) => {
    const sid = sessionId || verificationSessionId;
    if (!sid) return;
    
    setCheckingStatus(true);
    try {
      const res = await axios.get(
        `${API}/auth/check-verification/${sid}`,
        { withCredentials: true }
      );
      
      if (res.data.age_verified) {
        updateUser({ age_verified: true });
        toast.success(t('toasts.verificationSuccess'));
        setVerificationSessionId(null);
      } else if (res.data.status === "Declined") {
        toast.error(t('toasts.verificationDeclined'));
        setVerificationSessionId(null);
      }
    } catch (err) {
      toast.error(t('toasts.errorOccurred'));
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-24 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Profile Header */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 rounded-full bg-[#1a4d2e] flex items-center justify-center mx-auto mb-4 relative">
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <User className="w-12 h-12 text-white" />
            )}
            {user.age_verified && (
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
            )}
          </div>
          
          {isEditing ? (
            <div className="space-y-3 max-w-xs mx-auto">
              <Input
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                placeholder={t('auth.namePlaceholder')}
                data-testid="edit-name-input"
              />
              <Input
                type="email"
                value={editData.email}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                placeholder={t('auth.emailPlaceholder')}
                data-testid="edit-email-input"
              />
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-profile-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  {t('profile.save')}
                </Button>
                <Button
                  onClick={handleCancelEdit}
                  variant="outline"
                  data-testid="cancel-edit-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  {t('profile.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-[#111827]">{user.name}</h1>
              <p className="text-[#6b7280]">{user.email}</p>
              <button
                onClick={handleStartEdit}
                className="mt-2 text-sm text-[#1a4d2e] hover:underline flex items-center gap-1 mx-auto"
                data-testid="edit-profile-btn"
              >
                <Edit2 className="w-3 h-3" />
                {t('profile.editProfile')}
              </button>
            </>
          )}
        </div>

        {/* Age Verification Section */}
        <div className="bg-[#f2f4f3] rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              user.age_verified ? "bg-green-500" : "bg-[#1a4d2e]"
            }`}>
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-[#111827]">{t('ageVerification.title')}</h2>
              <p className="text-sm text-[#6b7280]">
                {user.age_verified ? t('ageVerification.verified') : t('ageVerification.notVerified')}
              </p>
            </div>
            {user.age_verified && (
              <CheckCircle2 className="w-6 h-6 text-green-500 ml-auto" />
            )}
          </div>

          {user.age_verified ? (
            <div className="p-4 rounded-xl bg-green-50 border border-green-200">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">{t('ageVerification.verificationComplete')}</p>
                  <p className="text-sm text-green-600">{t('ageVerification.verifiedAs18')}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-[#6b7280]">
                {t('ageVerification.description')}
              </p>

              <div className="bg-white/50 rounded-xl p-4 border border-gray-200">
                <h4 className="font-medium text-[#111827] mb-2">{t('ageVerification.whatToExpect')}</h4>
                <ul className="text-sm text-[#6b7280] space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4d2e] rounded-full"></span>
                    {t('ageVerification.step1')}
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4d2e] rounded-full"></span>
                    {t('ageVerification.step2')}
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4d2e] rounded-full"></span>
                    {t('ageVerification.step3')}
                  </li>
                </ul>
              </div>

              <Button
                onClick={startAgeVerification}
                disabled={startingVerification}
                className="w-full btn-primary py-3 rounded-xl font-semibold"
                data-testid="start-verification-btn"
              >
                {startingVerification ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {t('ageVerification.starting')}
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    {t('ageVerification.startVerification')}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              {verificationSessionId && (
                <Button
                  onClick={() => checkVerificationStatus()}
                  disabled={checkingStatus}
                  variant="outline"
                  className="w-full py-3 rounded-xl font-semibold border-2 border-[#1a4d2e] text-[#1a4d2e]"
                  data-testid="check-status-btn"
                >
                  {checkingStatus ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      {t('ageVerification.checking')}
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {t('ageVerification.checkStatus')}
                    </>
                  )}
                </Button>
              )}

              <p className="text-center text-xs text-[#9ca3af]">
                {t('ageVerification.poweredBy')}
              </p>
            </div>
          )}
        </div>

        {/* Store QR Code */}
        <div className="bg-[#f2f4f3] rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#1a4d2e] flex items-center justify-center">
              <Store className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-[#111827]">{t('profile.storeAuth')}</h2>
              <p className="text-sm text-[#6b7280]">
                {t('profile.storeAuthSubtitle')}
              </p>
            </div>
          </div>

          <div className="flex flex-col items-center">
            <div className="bg-white p-4 rounded-xl shadow-sm mb-4">
              <QRCodeSVG
                value={`https://pxm.ro/u/${user.user_id}`}
                size={180}
                level="H"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#1a4d2e"
                data-testid="store-qr-code"
              />
            </div>
            <p className="text-xs text-[#6b7280] text-center">
              {t('profile.showQrCode')}
            </p>
            <p className="mono text-xs text-[#9ca3af] mt-1">
              {user.user_id}
            </p>
          </div>
        </div>

        {/* Account Info */}
        <div className="bg-[#f2f4f3] rounded-2xl p-6 mb-6">
          <h2 className="font-semibold text-[#111827] mb-4">{t('profile.accountInfo')}</h2>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-[#6b7280]">{t('profile.userId')}</span>
              <span className="mono text-[#111827]">{user.user_id}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#6b7280]">{t('profile.ageVerified')}</span>
              <span className={`font-medium ${user.age_verified ? "text-green-600" : "text-[#9ca3af]"}`}>
                {user.age_verified ? t('profile.yes') : t('profile.no')}
              </span>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <Button
          data-testid="logout-btn"
          onClick={handleLogout}
          variant="outline"
          className="w-full border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-colors rounded-xl py-3"
        >
          <LogOut className="w-4 h-4 mr-2" />
          {t('profile.signOut')}
        </Button>
      </div>
    </div>
  );
};
